import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Table, TableRowSelectEvent } from 'primeng/table';
import { Subject, takeUntil } from 'rxjs';
import { LookupService } from 'src/app/services/lookup.service';
import { PeopleService } from 'src/app/services/people.service';
import { ResultsService } from 'src/app/services/results.service';
import { AppConstants } from 'src/app/shared/app.constants';
import { NESSValidators } from 'src/app/shared/validators/ness.validators';

@Component({
  selector: 'ness-result-defendant-form',
  templateUrl: './result-defendant-form.component.html'
})
export class ResultDefendantFormComponent implements OnDestroy, OnInit {
  @Output() closeForm = new EventEmitter<ServerModels.ENESS_XREF_SuccessStoriesDefendants>();
  @Input() _defendant: ServerModels.ENESS_XREF_SuccessStoriesDefendants;
  prosecutionTypeControl = new FormControl('');
  adjudicationStatusControl: FormControl = new FormControl('', Validators.required);
  defendantInfoForm: FormGroup = this.fb.group({
    PersonID: ['', Validators.required],
    ENESS_Person: this.fb.group({
      FirstName: [{ value: '', disabled: true }, Validators.required],
      LastName: [{ value: '', disabled: true }, Validators.required]
    }),
    SuccessStoryProsecutionTypeID: this.prosecutionTypeControl,
    StateID: ['', NESSValidators.requiredIf({ value: !!this.prosecutionTypeControl.value })],
    JudicialDistrictID: ['', NESSValidators.requiredIf({ value: !!this.prosecutionTypeControl.value })],
    ArrestDate: '',
    ConvictionDate: '',
    SentencingDate: '',
    SentenceInMonths: '',
    NumberOfShootingsChargedOrIndicted: null,
    Charges: '',
    SuccessStoryAdjudicationStatusID: this.adjudicationStatusControl
  });
  @ViewChild(Table, { static: false }) dataTable: Table;
  ENESS_Person: FormGroup;
  federalProsecutionID: string;
  filterTypes: ServerModels.ENESS_PersonFilters[];
  findAPersonForm: FormGroup = this.fb.group({
    FilterID: ['', Validators.required],
    FilterInput: '',
    FilterDate: new FormControl<Date>(null),
    FilterSSN: '',
    FilterFirstNameInput: '',
    FilterLastNameInput: '',
    FilterPhoneNumber: ''
  });
  findPeopleResults: ServerModels.ENESS_PersonDTO[] = [];
  isJudicialDistrict: boolean = false;
  isStateProsecution: boolean = false;
  judicialDistricts: ServerModels.ENESS_REF_JudicialDistricts[];
  maxDateRule: Date = new Date();
  @Output() onDefendantsAdded = new EventEmitter<ServerModels.ENESS_XREF_SuccessStoriesDefendants>();
  peopleFilterValues: ServerModels.FilterValues[] = [];
  peopleSearchResultTableCols: any[];
  _person: ServerModels.ENESS_PersonDTO;
  rows: number = AppConstants.defaultTableRows;
  selectedPeople: ServerModels.ENESS_Person;
  showFilterInputCtrl: boolean;
  showFilterDateCtrl: boolean;
  showFilterSSNCtrl: boolean;
  showFilterNameCtrl: boolean;
  showFilterPhoneNumberCtrl: boolean;
  @Output() showPerson = new EventEmitter<ServerModels.ENESS_PersonDTO>();
  stateProsecutionID: string;
  adjudicationStatus: ServerModels.ENESS_REF_SuccessStoryAdjudicationStatuses = null;

  private _unsubscribe$: Subject<void> = new Subject<void>();
  private _formLoaded: boolean = false;

  constructor(
    public lookupService: LookupService,
    public peopleService: PeopleService,
    public resultService: ResultsService,
    private fb: FormBuilder) { }

  ngOnInit() {
    this.peopleSearchResultTableCols = [
      { field: 'LastName', header: 'Last Name' },
      { field: 'FirstName', header: 'First Name' },
      { field: 'DateOfBirth', header: 'Birth Date' },
      { field: 'SSN', header: 'SSN' },
      { field: 'ENESS_XREF_PeopleFBIFileNumbers', subfield: 'FBIFileNumber', header: 'FBI #' },
      { field: 'ENESS_XREF_PeopleStateIDNumbers', subfield: 'StateIDNumbers', header: 'State ID' }
    ];

    this.prosecutionTypeControl.valueChanges.pipe(takeUntil(this._unsubscribe$)).subscribe((val: string) => {
      if (val) {
        let prosecutionType: string = this.lookupProsecutionType(val).SuccessStoryProsecutionType;
        this.isJudicialDistrict = prosecutionType === 'Federal Prosecution';
        this.isStateProsecution = prosecutionType === 'State Prosecution';
      } else {
        this.isJudicialDistrict = false;
        this.isStateProsecution = false;
      }
      this.defendantInfoForm.get('StateID').reset({ value: '', disabled: !this.isStateProsecution });
      this.defendantInfoForm.get('JudicialDistrictID').reset({ value: '', disabled: !this.isJudicialDistrict });
      this.defendantInfoForm.updateValueAndValidity();
    });

    this.adjudicationStatusControl.valueChanges.pipe(takeUntil(this._unsubscribe$)).subscribe((val: string) => {
      this.prosecutionTypeControl.reset('');
      if (!!val) {
        this.adjudicationStatus = this.lookupAdjudicationStatusByName('Adjudicated');
        if (val == this.adjudicationStatus?.SuccessStoryAdjudicationStatusID) {
          this.prosecutionTypeControl.setValidators(Validators.required);
        } else {
          this.prosecutionTypeControl.clearValidators();
        }
        this.prosecutionTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: false });
      }
    });

    this.resultService.getJudicialDistricts().pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (districts: ServerModels.ENESS_REF_JudicialDistricts[]) => {
        this.judicialDistricts = districts;
        this.stateProsecutionID = this.resultService.prosecutionTypesSubj.getValue().find((x: ServerModels.ENESS_REF_SuccessStoryProsecutionTypes) => x.SuccessStoryProsecutionType.toLowerCase() === 'state prosecution')?.SuccessStoryProsecutionTypeID ?? '';
        this.federalProsecutionID = this.resultService.prosecutionTypesSubj.getValue().find((x: ServerModels.ENESS_REF_SuccessStoryProsecutionTypes) => x.SuccessStoryProsecutionType.toLowerCase() === 'federal prosecution')?.SuccessStoryProsecutionTypeID ?? '';
      }
    });
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  editDefendant() {
    if (this._defendant) {
      setTimeout(() => {
        this.defendantInfoForm.patchValue({
          PersonID: this._defendant.PersonID,
          ENESS_Person: {
            FirstName: this._defendant.ENESS_Person.FirstName,
            LastName: this._defendant.ENESS_Person.LastName
          },
          SuccessStoryAdjudicationStatusID: this._defendant.SuccessStoryAdjudicationStatusID,
          SuccessStoryProsecutionTypeID: this._defendant.SuccessStoryProsecutionTypeID || '',
          StateID: this._defendant.StateID,
          JudicialDistrictID: this._defendant.JudicialDistrictID,
          ArrestDate: this._defendant.ArrestDate == null ? '' : new DatePipe('en-US').transform(this._defendant.ArrestDate, 'MM/dd/yyyy HH:mm'),
          ConvictionDate: this._defendant.ConvictionDate == null ? '' : new DatePipe('en-US').transform(this._defendant.ConvictionDate, 'MM/dd/yyyy HH:mm'),
          SentencingDate: this._defendant.SentencingDate == null ? '' : new DatePipe('en-US').transform(this._defendant.SentencingDate, 'MM/dd/yyyy HH:mm'),
          SentenceInMonths: this._defendant.SentenceInMonths,
          NumberOfShootingsChargedOrIndicted: this._defendant.NumberOfShootingsChargedOrIndicted,
          Charges: this._defendant.Charges,
          ENESS_REF_SuccessStoryProsecutionTypes: this._defendant.ENESS_REF_SuccessStoryProsecutionTypes,
          ENESS_REF_SuccessStoryAdjudicationStatuses: this._defendant.ENESS_REF_SuccessStoryAdjudicationStatuses
        });
        this.defendantInfoForm.updateValueAndValidity();
      });
    }
  }

  onArrestDateBlur(dateValue: string) {
    let futureDate: boolean = Date.parse(dateValue) > Date.now();
    if (futureDate) {
      this.defendantInfoForm.get('ArrestDate').setErrors({
        'max': futureDate
      });
    }
  }

  onConvictionDateBlur(dateValue: string) {
    let futureDate: boolean = Date.parse(dateValue) > Date.now();
    if (futureDate) {
      this.defendantInfoForm.get('SentenceDate').setErrors({
        'max': futureDate
      });
    }
  }

  onSentenceDateBlur(dateValue: string) {
    let futureDate: boolean = Date.parse(dateValue) > Date.now();
    if (futureDate) {
      this.defendantInfoForm.get('ConvictionDate').setErrors({
        'max': futureDate
      });
    }
  }

  // Find person section
  onSearchPerson() {
    const searchFilterId: string = this.findAPersonForm.get('FilterID').value;
    if (!searchFilterId)
      return;


    let filterValue: ServerModels.FilterValues = {
      FilterColumnName: searchFilterId,
      FilterValue: null,
      DateFilterValue: null
    } as ServerModels.FilterValues;

    if (searchFilterId === 'Name') {
      filterValue.FilterValue = (!!this.findAPersonForm.get('FilterFirstNameInput').value && !!this.findAPersonForm.get('FilterLastNameInput').value) ? `${this.findAPersonForm.get('FilterFirstNameInput').value} ${this.findAPersonForm.get('FilterLastNameInput').value}`.trim() : (!!this.findAPersonForm.get('FilterFirstNameInput').value) ? `FirstName:${this.findAPersonForm.get('FilterFirstNameInput').value.trim()}` : `LastName:${this.findAPersonForm.get('FilterLastNameInput').value.trim()}`;
    } else if (searchFilterId === 'DateOfBirth') {
      filterValue.FilterValue = this.findAPersonForm.get('FilterDate').value;
    } else if (searchFilterId === 'SSN') {
      filterValue.FilterValue = this.findAPersonForm.get('FilterSSN').value.trim().replace(/_/g, '');
    } else if (searchFilterId === 'PhoneNumber') {
      let phone = this.findAPersonForm.get('FilterPhoneNumber').value;
      if (!!phone) {
        phone = phone.trim().replace(/\D/g, ''); // keep only digits
        if (phone.length > 3) {
          filterValue.FilterValue = phone;
        }
      }
    } else {
      filterValue.FilterValue = this.findAPersonForm.get('FilterInput').value.trim();
    }

    // Create API payload
    this.peopleFilterValues = [];
    this.peopleFilterValues.push(filterValue);

    if (this.peopleFilterValues != null && this.peopleFilterValues.length > 0) {
      // Call search people API
      this.peopleService.findPeople(this.peopleFilterValues).pipe(takeUntil(this._unsubscribe$)).subscribe({
        next: (people: ServerModels.ENESS_PersonDTO[]) => {
          this.findPeopleResults = people;
        },
        complete: () => {
          this.findAPersonForm.reset({
            FilterID: ''
          })
          this.resetFilterCtrl();
          this.showFilterInputCtrl = true;
        }
      });
    }
  }

  onFindPeopleFilterChange(event: Event) {
    let selectEl = event.target as HTMLSelectElement;
    const filteredValue: string = selectEl.options[selectEl.selectedIndex].text;
    this.resetFilterCtrl();

    switch (filteredValue) {
      case "Date of Birth":
        this.showFilterDateCtrl = true;
        break;
      case "Social Security Number":
        this.showFilterSSNCtrl = true;
        break;
      case "Name":
        this.showFilterNameCtrl = true;
        break;
      case "Phone #":
        this.showFilterPhoneNumberCtrl = true;
        break;
      default:
        this.showFilterInputCtrl = true;
    }
  }

  viewPersonDetails(rowData: ServerModels.ENESS_PersonDTO) {
    this.peopleService.getPersonDetailsDTOById(rowData.PersonID).pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (person: ServerModels.ENESS_PersonDTO) => {
        this._person = person;
        this.showPerson.emit(person);
      }
    });
  }

  lookupProsecutionType(id: string): ServerModels.ENESS_REF_SuccessStoryProsecutionTypes {
    return this.resultService.prosecutionTypesSubj.getValue().find((item: ServerModels.ENESS_REF_SuccessStoryProsecutionTypes) => { return item.SuccessStoryProsecutionTypeID.toString().toLowerCase() == id.toLowerCase() });
  }

  lookupAdjudicationStatusByName(status: string): ServerModels.ENESS_REF_SuccessStoryAdjudicationStatuses {
    return this.resultService.adjudicationStatusesSubj.getValue().find((x: ServerModels.ENESS_REF_SuccessStoryAdjudicationStatuses) => x.SuccessStoryAdjudicationStatus == status);
  }

  lookupAdjudicationStatusByID(id: string): ServerModels.ENESS_REF_SuccessStoryAdjudicationStatuses {
    return this.resultService.adjudicationStatusesSubj.getValue().find((item: ServerModels.ENESS_REF_SuccessStoryAdjudicationStatuses) => { return item.SuccessStoryAdjudicationStatusID.toString().toLowerCase() == id.toLowerCase() });
  }

  onRowSelect(event: TableRowSelectEvent) {
    this._defendant = this.defendantInfoForm.value as ServerModels.ENESS_XREF_SuccessStoriesDefendants;
    this._defendant.ENESS_Person = event["data"] as ServerModels.ENESS_Person;

    if (this._defendant.ENESS_Person != null) {
      this.defendantInfoForm.reset({
        StateID: { value: '', disabled: true },
        SuccessStoryProsecutionTypeID: '',
        SuccessStoryAdjudicationStatusID: '',
        JudicialDistrictID: { value: '', disabled: true }
      });
      this.defendantInfoForm.patchValue({
        PersonID: this._defendant.ENESS_Person.PersonID,
        ENESS_Person: {
          FirstName: this._defendant.ENESS_Person.FirstName,
          LastName: this._defendant.ENESS_Person.LastName
        }
      });
    }

    this.clearAllFields();
  }

  clearAllFields() {
    this.findAPersonForm.reset({
      FilterID: ''
    });
    this.findPeopleResults = [];
    this.selectedPeople = null;
  }

  onSaveDefendant() {
    if (this.defendantInfoForm.valid) {
      const formModel = this.defendantInfoForm.getRawValue();
      this._defendant = Object.assign({}, this._defendant, formModel);
      this._defendant.ENESS_REF_SuccessStoryProsecutionTypes = formModel.SuccessStoryProsecutionTypeID ? this.lookupProsecutionType(formModel.SuccessStoryProsecutionTypeID) : null;
      this._defendant.ENESS_REF_SuccessStoryAdjudicationStatuses = formModel.SuccessStoryAdjudicationStatusID ? this.lookupAdjudicationStatusByID(formModel.SuccessStoryAdjudicationStatusID) : null;
      this._defendant.ENESS_REF_State = formModel.StateID ? this.lookupService.usStatesSubj.getValue().find((s: ServerModels.ENESS_REF_State) => s.StateID.toLowerCase() === formModel.StateID.toLowerCase()) : null;
      this._defendant.ENESS_REF_JudicialDistricts = formModel.JudicialDistrictID ? this.judicialDistricts.find(d => d.JudicialDistrictID.toLowerCase() === formModel.JudicialDistrictID.toLowerCase()) : null;
      this.onDefendantsAdded.emit(this._defendant);
      this.clearFields();
    }
  }

  onClose() {
    this.clearFields();
    this.closeForm.emit(this._defendant);
  }

  private clearFields() {
    this.defendantInfoForm.reset({
      StateID: { value: '', disabled: true },
      SuccessStoryProsecutionTypeID: '',
      SuccessStoryAdjudicationStatusID: '',
      JudicialDistrictID: { value: '', disabled: true },
      SuccessStoryAdjudicationTypesID: ''
    });
    this.findAPersonForm.reset({
      FilterID: ''
    });
    this.findPeopleResults = [];
  }

  private resetFilterCtrl() {
    this.showFilterInputCtrl = false;
    this.showFilterDateCtrl = false;
    this.showFilterSSNCtrl = false;
    this.showFilterNameCtrl = false;
    this.showFilterPhoneNumberCtrl = false;
  }

  public valueRangeCheck(): void {
    if (this.defendantInfoForm.get('NumberOfShootingsChargedOrIndicted').value > 999) {
      this.defendantInfoForm.get('NumberOfShootingsChargedOrIndicted').setValue(999, { onlySelf: true, emitEvent: false });
    }
  }
}
